import Button from 'components/base/Button';
import { useAccessToken } from 'providers/AccessTokenProvider';
import React, { ChangeEvent, FormEvent, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { CommentType, CREATE_COMMENT } from 'services/api/comment';
import { Job } from 'services/api/job';
import MentionInput from './MentionInput';
import { MentionUser } from 'types/mention';

const CommentForm = ({
  job,
  setHasUpdated,
  canAddCommentsField,
  canAddShopCommentsField,
  canAddJobStageCommentsField
}: {
  job?: Job;
  setHasUpdated?: any;
  canAddCommentsField?: boolean;
  canAddShopCommentsField?: boolean;
  canAddJobStageCommentsField?: boolean;
}) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [content, setContent] = useState<string>('');
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [commentType, setCommentType] = useState<CommentType>(CommentType.Shop);
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([]);
  const { authUser } = useAccessToken();
  const queries = useQueryClient();

  const createComment = useMutation('createComment', CREATE_COMMENT, {
    onSuccess: () => {
      setIsLoading(false);
      // Force immediate refetch instead of just invalidating
      queries.refetchQueries('getJobs');
      queries.refetchQueries(['jobComments', job?.id || job?._id]);
      if (isSticky) {
        queries.refetchQueries(['stickyComments', job?.id || job?._id]);
      }
      setHasUpdated?.(true);
      setContent('');
      setIsSticky(false);
      setCommentType(CommentType.Field);
      setMentionedUsers([]);
    },
    onError: () => {
      setIsLoading(false);
    }
  });

  const handleCommentSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }
    setIsLoading(true);

    const payload: any = {
      content,
      jobId: (job?.id || job?._id) as string,
      creator: (authUser?.id || authUser?._id) as string,
      commentType: commentType,
      isSticky: isSticky
    };

    if (mentionedUsers.length > 0) {
      payload.mentionedUsers = mentionedUsers.map(user => user._id);
    }

    createComment.mutateAsync(payload);
  };

  const handleMentionChange = (
    value: string,
    mentionedUsers: MentionUser[]
  ) => {
    setContent(value);
    setMentionedUsers(mentionedUsers);
  };

  const handleCommentTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCommentType(e.target.value as unknown as CommentType);
  };

  const handleStickyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsSticky(e.target.checked);
  };

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleCommentSubmit}>
        <Form.Group className="mb-3" controlId="commentForm">
          <MentionInput
            value={content}
            onChange={handleMentionChange}
            placeholder="Add a comment... Type @ to mention someone"
            rows={3}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <div className="d-flex align-items-center gap-3">
            {canAddShopCommentsField && (
              <Form.Check
                type="radio"
                id="shop-comment"
                label="Shop"
                name="commentType"
                value="Shop"
                checked={commentType === 'Shop'}
                onChange={handleCommentTypeChange}
                inline
              />
            )}
            {canAddJobStageCommentsField && (
              <Form.Check
                type="radio"
                id="job-stage-comment"
                label="Job Stage"
                name="commentType"
                value="JobStage"
                checked={commentType === 'JobStage'}
                onChange={handleCommentTypeChange}
                inline
              />
            )}
            {canAddCommentsField && (
              <Form.Check
                type="radio"
                id="field-comment"
                label="Field"
                name="commentType"
                value="Field"
                checked={commentType === 'Field'}
                onChange={handleCommentTypeChange}
                inline
              />
            )}
          </div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="sticky-comment"
            label="Pinned"
            checked={isSticky}
            onChange={handleStickyChange}
          />
        </Form.Group>
        {canAddCommentsField ||
        canAddShopCommentsField ||
        canAddJobStageCommentsField ? (
          <div className="d-flex align-items-center gap-3">
            <Button
              type="submit"
              variant="primary"
              className="px-6 ms-auto"
              onClick={handleCommentSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Comment'}
            </Button>
          </div>
        ) : null}
      </Form>
    </>
  );
};

export default CommentForm;
